import { BaseText, ListItem, Meta, theme } from 'aimo-ui';
import Icon from 'components/icons/Icon';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { usePaymentStore } from 'stores/paymentStore';
import { CenterRow, StyledWrapper } from 'styles/ContainerStyles';
import { PaymentOption } from 'types/generatedSchemaTypes';
import { testIdentifiers } from 'utils/testIdentifiers';
import styled from 'styled-components/native';
import { useUiStore } from 'stores/uiStore';

// Extended PaymentOption type with optionName
type EnrichedPaymentOption = PaymentOption & {
  optionName?: string;
};

const TitleWrapper = styled.View`
  flex-direction: row;
`;

const ArrowWrapper = styled.View`
  margin-right: -4px;
  margin-top: 4px;
`;

const TitleComponent: React.FC<{
  item: EnrichedPaymentOption;
  indexValue: number;
}> = ({ item, indexValue }) => {
  const { t } = useTranslation();

  const getDisplayName = () => {
    return item?.optionName;
  };

  const getRegistrationPlateText = () => {
    if (!item.registrationPlates || item.registrationPlates.length === 0) {
      return t('paymentMethods.anyVehicle');
    }

    if (item.registrationPlates.length === 1) {
      return item.registrationPlates[0]?.plateText ?? '';
    }

    return t('paymentMethods.vehicleCount', {
      count: item.registrationPlates.length,
    });
  };

  return (
    <TitleWrapper>
      <StyledWrapper mb={0} maxWidth={230}>
        <BaseText
          testID={`corporate-account-title-${indexValue}`}
          numberOfLines={1}>
          {getDisplayName()}
        </BaseText>
        <Meta numberOfLines={1} color={theme.colors.gray300}>
          {getRegistrationPlateText()}
        </Meta>
      </StyledWrapper>
    </TitleWrapper>
  );
};

const RightContentComponent = ({
  indexValue,
}: {
  item: EnrichedPaymentOption;
  indexValue: number;
}) => (
  <CenterRow>
    <ArrowWrapper>
      <Icon
        name="Chevron_Right"
        {...testIdentifiers(`corporate-account-${indexValue}-chevron-right`)}
        size="s"
      />
    </ArrowWrapper>
  </CenterRow>
);

const CorporateAccountPaymentOptionItem = ({
  item,
  indexValue,
}: {
  item: EnrichedPaymentOption;
  indexValue: number;
}) => {
  const IconComponent = useCallback(
    () => (
      <Icon size="l" name="Business-Filled" color={theme.colors.boost.green} />
    ),
    []
  );

  const handlePress = useCallback(() => {
    usePaymentStore.getState().setSelectedCorporateAccountPaymentOption({
      optionName: item.optionName ?? '',
      registrationPlates: (item.registrationPlates ?? []).map((plate) => ({
        plateText: plate?.plateText ?? null,
      })),
      validFrom: item.validity?.validFrom ?? null,
      validTo: item.validity?.validTo ?? null,
      method: item.method ?? 'CORPORATE_ACCOUNT',
    });
    useUiStore.getState().setSheetModal('corporateAccount');
  }, [item]);

  return (
    <ListItem
      item={item}
      indexValue={indexValue}
      onPress={handlePress}
      iconComponent={IconComponent}
      titleComponent={() => TitleComponent({ item, indexValue })}
      rightContentComponent={() => RightContentComponent({ indexValue })}
      accessibilityRole="button"
      accessible={true}
    />
  );
};

export default CorporateAccountPaymentOptionItem;
